import _ from "lodash";
import classnames from "classnames";
import {
    CellValueChangedEvent,
    ColDef,
    ICellRendererParams,
    ICellEditorParams,
    EditableCallbackParams,
} from "ag-grid-community";
import { ValueFormatterParams, ValueGetterParams } from "ag-grid-community/dist/lib/entities/colDef";
import { FdmAttribute } from "@nuorder/client-cart-api/dist/types/modules/core/models/fdm-attribute";
import { SchemaField, SchemaFieldFormatter, SchemaFieldSubtype, SchemaFieldType } from "types/schema";
import { InputRendererProps } from "containers/legacy-cart/grid-components/InputRenderer";
import { updateLineItem, addLineItem, updateWorkingOrder } from "actions/cart-legacy";
import { SizingViewComponents } from "constants/sizingView";
import { postToWindow } from "helpers/window-messages";
import { isMatchLocation } from "helpers/legacy-cart/bulk";
import { KEYBOARD_KEYS } from "constants/keyboard";
import { ColumnTypes, IRowData } from "../types";
import { mapLineItem, mapLineItemFDMField } from "containers/legacy-cart/helpers";
import { isEqualBy } from "helpers/common";

type FdmValue = {
    value: string;
    isEditable: boolean;
    hasError: boolean;
};

enum FdmCellType {
    Text = "text",
    String = "string",
    Select = "select",
    Multiselect = "multiselect",
}

const popupTypes = new Set([FdmCellType.Select, FdmCellType.Multiselect]);

export const buildFdmColDef = (field: SchemaField): ColDef => {
    const key = mapLineItemFDMField(field);
    const cellType = getCellType(field);
    if (!cellType) {
        return null;
    }
    return {
        headerName: field.name,
        colId: key,
        type: ColumnTypes.FdmAttribute,
        filter: false,
        suppressMenu: true,
        suppressColumnsToolPanel: true,
        suppressMovable: true,
        singleClickEdit: true,
        resizable: true,
        cellEditorPopup: popupTypes.has(cellType),
        headerClass: classnames("fdm-header", { required: field.required }),
        cellClass: ({ value }: { value: FdmValue }) =>
            classnames("fdm-cell", `type-${cellType}`, { disabled: !value.isEditable, hasError: value.hasError }),
        width: 140,
        suppressKeyboardEvent: (params) =>
            popupTypes.has(cellType) && params.event.key === KEYBOARD_KEYS.ENTER && params.editing, // allow selection with Enter
        editable: ({ node, data }: EditableCallbackParams) =>
            !isBottomRow(node) && cellType !== FdmCellType.Text && isEditable(data),
        cellEditorSelector: ({ node, value, data }: ICellEditorParams) =>
            isBottomRow(node) ? null : Components[cellType]?.editor({ field, value, data }) || null,
        cellRendererSelector: ({ node, value, data }: ICellRendererParams) =>
            isBottomRow(node) ? null : Components[cellType]?.renderer({ field, value, data }) || null,
        equals: isEqualBy<FdmValue>(["value", "isEditable", "hasError"]),
        valueGetter: ({ data, node }: CastProp<ValueGetterParams, "data", IRowData>) =>
            isBottomRow(node) ? "" : valueGetter(key, data),
        valueSetter: ({ data, newValue }) => {
            data.lineItemAttributes[key].value = newValue || "";
            return true;
        },
        valueFormatter: ({ value }: ValueFormatterParams) => applyFormatters(value?.value ?? value, field).toString(),
        onCellValueChanged: ({
            data,
            newValue,
            oldValue,
        }: CastProp<CastProp<CellValueChangedEvent, "newValue", FdmValue>, "oldValue", FdmValue>) => {
            if (oldValue.value === newValue.value || !data?.item) {
                return;
            }
            postToWindow(
                updateWorkingOrder(
                    data.isVirtual
                        ? addLineItem(data.item.id, mapLineItem(data))
                        : updateLineItem(data.item.id, { [key]: newValue.value })
                )
            );
        },
    };
};

const valueGetter = (key: string, data: IRowData) => {
    const field = data?.lineItemAttributes?.[key];
    return {
        value: field?.value || "",
        isEditable: isEditable(data),
        hasError: field?.hasError && data?.item.hasError,
    };
};

const isEditable = (data: IRowData) => data?.item && isMatchLocation(data);

const isBottomRow = (node: ICellEditorParams["node"]) => node.rowPinned === "bottom";

interface ComponentParams {
    field: SchemaField;
    value: FdmValue;
    data: IRowData;
}

const Components = {
    [FdmCellType.Text]: {
        renderer: () => ({
            component: "agGroupCellRenderer",
        }),
        editor: () => null,
    },
    [FdmCellType.String]: {
        renderer: ({ field: { details } }: ComponentParams) => ({
            component: SizingViewComponents.InputRenderer,
            params: { input: { dataTest: `fdm-${details.type}-${details.subtype}` } } as InputRendererProps,
        }),
        editor: ({ value }: ComponentParams) => ({
            component: "agTextCellEditor",
            params: { value: _.toString(value.value) },
        }),
    },
    [FdmCellType.Select]: {
        renderer: () => ({
            component: SizingViewComponents.SelectRenderer,
        }),
        editor: ({ field, data }: ComponentParams) => ({
            component: SizingViewComponents.SelectEditor,
            params: {
                values: mapOptions(field, getSelectOptions(field, data)),
            },
        }),
    },
    [FdmCellType.Multiselect]: {
        renderer: () => ({
            component: SizingViewComponents.SelectRenderer,
        }),
        editor: ({ field, data }: ComponentParams) => ({
            component: SizingViewComponents.MultiSelectEditor,
            params: {
                options: mapOptions(field, getSelectOptions(field, data)),
                formatValue: ({ value }) => value,
            },
        }),
    },
};

const getSelectOptions = (field: SchemaField, data: IRowData) => {
    const { subtype, type, allowed_values } = field.details;
    const showEmptyValue = (!field.required || field.no_default) && type === SchemaFieldType.Single;

    if (subtype === SchemaFieldSubtype.String && !_.isEmpty(allowed_values)) {
        const options = _.map(allowed_values, "value");
        return showEmptyValue ? ["", ...options] : options;
    }

    if (subtype === SchemaFieldSubtype.Lookup && field.details.lookup.startsWith("product.")) {
        const options = data.lineItemAttributes?.[_.last(field.key.split("line_items."))]?.lookupValue;
        if (!_.isArray(options)) {
            return [];
        }
        return showEmptyValue ? ["", ...options] : options;
    }

    return [];
};

const mapOptions = (field: SchemaField, options: string[]) =>
    _.map(options, (value) => ({ label: applyFormatters(value, field), value, data: value }));

const getCellType = (field: SchemaField): FdmCellType => {
    const { subtype, type, allowed_values } = field.details;
    if (subtype === SchemaFieldSubtype.String && type === SchemaFieldType.Single) {
        return _.isEmpty(allowed_values) ? FdmCellType.String : FdmCellType.Select;
    }
    if (subtype === SchemaFieldSubtype.String && type === SchemaFieldType.Multi) {
        return _.isEmpty(allowed_values) ? FdmCellType.String : FdmCellType.Multiselect;
    }
    if (type === SchemaFieldType.Single && subtype === SchemaFieldSubtype.Lookup) {
        return FdmCellType.Select;
    }
    if (type === SchemaFieldType.Multi && subtype === SchemaFieldSubtype.Lookup) {
        return FdmCellType.Multiselect;
    }
    return undefined;
};

const applyFormatters = (value: FdmAttribute["value"], field: SchemaField) =>
    _.reduce(field.details.formatters, (value, formatter) => FORMATTERS[formatter]?.(value) ?? value, value);

const FORMATTERS = {
    [SchemaFieldFormatter.Uppercase]: (x) => _.toString(x).toUpperCase(),
    [SchemaFieldFormatter.Lowercase]: (x) => _.toString(x).toLowerCase(),
};
